import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ArrowRight, ShoppingBag, CreditCard, Upload, CheckCircle, MapPin, Phone, FileText, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { removeFromCart, getPaymentMethodsGlobal, createOrder, uploadOrderProof, getMyOrders } from '../utils/api';
import { toast } from 'sonner';

const Cart = () => {
  const { cartItems, refreshCart } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [step, setStep] = useState(1); // 1: Cart, 2: Checkout, 3: Payment Upload, 4: Confirmation
  const [paymentMethods, setPaymentMethods] = useState({});
  const [selectedPayment, setSelectedPayment] = useState('gcash');
  const [shippingAddress, setShippingAddress] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [hasSouvenirs, setHasSouvenirs] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const calculateTotal = () => {
      const sum = cartItems.reduce((acc, item) => {
        return acc + (item.product.price * item.cart_item.quantity);
      }, 0);
      setTotal(sum);
      
      // Check if cart has souvenirs
      const souvenirs = cartItems.some(item => item.product.category === 'souvenir');
      setHasSouvenirs(souvenirs);
    };
    calculateTotal();
  }, [cartItems]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await getPaymentMethodsGlobal();
        setPaymentMethods(response.data);
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
      }
    };
    fetchPaymentMethods();
  }, []);

  const handleRemove = async (cartItemId) => {
    try {
      await removeFromCart(cartItemId);
      await refreshCart();
      toast.success('Item removed from cart');
    } catch (error) {
      console.error(error);
      toast.error('Failed to remove item');
    }
  };

  const handleProceedToCheckout = () => {
    if (!user) {
      toast.error('Please login to proceed with checkout');
      navigate('/login');
      return;
    }
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    setStep(2);
  };

  const handlePlaceOrder = async () => {
    if (hasSouvenirs && !shippingAddress.trim()) {
      toast.error('Please enter your shipping address');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.product.id,
          quantity: item.cart_item.quantity
        })),
        payment_method: selectedPayment,
        shipping_address: hasSouvenirs ? shippingAddress : null,
        contact_number: contactNumber || null
      };

      const response = await createOrder(orderData);
      setCreatedOrder({
        id: response.data.order_id,
        total: response.data.total_amount,
        paymentMethod: response.data.payment_method
      });
      await refreshCart();
      setStep(3);
      toast.success('Order placed! Please upload your proof of payment.');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploadingProof(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await uploadOrderProof(createdOrder.id, formData);
      toast.success('Proof of payment uploaded successfully!');
      setStep(4);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to upload proof');
    } finally {
      setUploadingProof(false);
    }
  };

  // Empty cart view
  if (cartItems.length === 0 && step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12" data-testid="empty-cart-message">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-slate-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Your cart is empty</h2>
          <p className="text-slate-600 mb-8">Add some items to get started!</p>
          <Button onClick={() => navigate('/products/ebooks')} className="btn-primary" data-testid="start-shopping-button">
            Start Shopping
          </Button>
        </div>
      </div>
    );
  }

  // Step 4: Order Confirmation
  if (step === 4) {
    return (
      <div className="min-h-screen py-12 bg-[#F9F9F7]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-slate-900 mb-4">Payment Proof Uploaded!</h1>
            <p className="text-slate-600 mb-6">
              Thank you for your order. Our team will verify your payment and process your order shortly.
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
              <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-[#8B1818] font-bold">1.</span>
                  Our team will verify your payment proof within 24-48 hours.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8B1818] font-bold">2.</span>
                  For eBooks: Download links will be sent to your email after approval.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#8B1818] font-bold">3.</span>
                  For Souvenirs: You'll receive a shipping notification with tracking number.
                </li>
              </ul>
            </div>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/products/ebooks')} variant="outline">
                Continue Shopping
              </Button>
              <Button onClick={() => navigate('/')} className="bg-[#8B1818] text-white hover:bg-[#701313]">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Upload Payment Proof
  if (step === 3 && createdOrder) {
    const paymentInfo = createdOrder.paymentMethod;
    return (
      <div className="min-h-screen py-12 bg-[#F9F9F7]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <h1 className="text-2xl font-bold text-slate-900 mb-6 text-center">Complete Your Payment</h1>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-6">
              <p className="text-amber-900 font-semibold">Order ID: {createdOrder.id.slice(0, 8).toUpperCase()}</p>
              <p className="text-amber-800 text-lg mt-1">Total: ₱{createdOrder.total.toFixed(2)}</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-slate-900 mb-3">{paymentInfo.name} Payment Instructions</h3>
              <div className="space-y-2 text-slate-700">
                <p><strong>Account Name:</strong> {paymentInfo.account_name}</p>
                <p><strong>Account Number:</strong> {paymentInfo.account_number}</p>
                <p className="text-sm text-slate-600 mt-3">{paymentInfo.instructions}</p>
              </div>
            </div>

            <div className="mb-6">
              <Label className="text-lg font-semibold mb-3 block">Upload Proof of Payment</Label>
              <label className="cursor-pointer block">
                <div className={`border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-[#8B1818] transition-colors ${uploadingProof ? 'opacity-50' : ''}`}>
                  <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                  <span className="text-slate-600 block mb-2">
                    {uploadingProof ? 'Uploading...' : 'Click to upload your payment screenshot/receipt'}
                  </span>
                  <span className="text-xs text-slate-400">PDF or Image files (JPG, PNG, WEBP)</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,image/*"
                    onChange={handleProofUpload}
                    className="hidden"
                    disabled={uploadingProof}
                  />
                </div>
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Data Retention Notice:</strong> Proofs of payment will be stored for 10 days from the delivery/completion date for verification purposes. After this period, the admin will delete them to protect your privacy.
              </p>
            </div>

            <p className="text-sm text-slate-500 text-center">
              Make sure your proof of payment clearly shows the transaction details including the amount and reference number.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Checkout Form
  if (step === 2) {
    return (
      <div className="min-h-screen py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => setStep(1)} 
            className="text-[#8B1818] mb-6 hover:underline flex items-center"
          >
            ← Back to Cart
          </button>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-8">Checkout</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Order Summary */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 h-fit">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Order Summary</h2>
              <div className="space-y-3 mb-4">
                {cartItems.map((item) => (
                  <div key={item.cart_item.id} className="flex justify-between items-center py-2 border-b border-slate-100">
                    <div>
                      <p className="font-medium text-slate-900">{item.product.name}</p>
                      <p className="text-sm text-slate-500">Qty: {item.cart_item.quantity}</p>
                    </div>
                    <p className="font-semibold">₱{(item.product.price * item.cart_item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-[#8B1818]">₱{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment & Shipping Form */}
            <div className="space-y-6">
              {/* Payment Method */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-[#8B1818]" />
                  Payment Method
                </h2>
                <div className="space-y-3">
                  {Object.entries(paymentMethods).map(([key, method]) => (
                    <label 
                      key={key}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedPayment === key 
                          ? 'border-[#8B1818] bg-[#8B1818]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={key}
                        checked={selectedPayment === key}
                        onChange={(e) => setSelectedPayment(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-semibold text-slate-900">{method.name}</p>
                        <p className="text-sm text-slate-500">{method.account_number}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Shipping Address (for souvenirs) */}
              {hasSouvenirs && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-[#8B1818]" />
                    Shipping Address
                  </h2>
                  <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg mb-4 flex items-center">
                    <Package className="h-4 w-4 mr-2 flex-shrink-0" />
                    Required for physical items (souvenirs) delivery
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address">Full Delivery Address *</Label>
                      <textarea
                        id="address"
                        value={shippingAddress}
                        onChange={(e) => setShippingAddress(e.target.value)}
                        placeholder="House/Unit No., Street, Barangay, City/Municipality, Province, ZIP Code&#10;&#10;Example: 123 Rizal St., Brgy. San Antonio, Makati City, Metro Manila, 1200"
                        className="mt-2 w-full min-h-[100px] px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent resize-none"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">Please provide complete address for accurate delivery</p>
                    </div>
                    <div>
                      <Label htmlFor="contact">Contact Number *</Label>
                      <Input
                        id="contact"
                        value={contactNumber}
                        onChange={(e) => setContactNumber(e.target.value)}
                        placeholder="09XX-XXX-XXXX"
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">For delivery coordination and updates</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Number (for ebooks only) */}
              {!hasSouvenirs && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-[#8B1818]" />
                    Contact Information
                  </h2>
                  <div>
                    <Label htmlFor="contact">Contact Number (Optional)</Label>
                    <Input
                      id="contact"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                      placeholder="09XX-XXX-XXXX"
                      className="mt-2"
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={handlePlaceOrder}
                disabled={loading}
                className="w-full bg-[#8B1818] text-white hover:bg-[#701313] py-6 text-lg"
              >
                {loading ? 'Processing...' : 'Place Order'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-xs text-slate-500 text-center">
                By placing this order, you agree to our Terms of Use and Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Cart View
  return (
    <div className="min-h-screen py-12 bg-[#F9F9F7]" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" data-testid="cart-heading">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
            {cartItems.map((item) => (
              <div
                key={item.cart_item.id}
                className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
                data-testid={`cart-item-${item.cart_item.id}`}
              >
                <div className="flex items-center space-x-4">
                  {/* Product Image */}
                  <div className="h-20 w-20 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
                    {item.product.image_url ? (
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#8B1818]/10 to-[#FDB913]/10">
                        <span className="text-xl text-[#8B1818] font-bold">
                          {item.product.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900" data-testid={`cart-item-name-${item.cart_item.id}`}>
                      {item.product.name}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {item.product.category === 'ebook' ? 'eBook (Digital)' : 'Souvenir (Physical)'}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      Quantity: {item.cart_item.quantity}
                    </p>
                    <p className="text-lg font-bold text-[#8B1818] mt-2" data-testid={`cart-item-price-${item.cart_item.id}`}>
                      ₱{(item.product.price * item.cart_item.quantity).toFixed(2)}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(item.cart_item.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                    data-testid={`remove-item-${item.cart_item.id}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal ({cartItems.length} items)</span>
                  <span data-testid="subtotal-amount">₱{total.toFixed(2)}</span>
                </div>
                {hasSouvenirs && (
                  <div className="flex justify-between text-slate-600">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-3 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-[#8B1818]" data-testid="total-amount">₱{total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={handleProceedToCheckout}
                className="w-full bg-[#8B1818] text-white hover:bg-[#701313]"
                data-testid="checkout-button"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 text-center">
                  <FileText className="h-4 w-4 inline mr-1" />
                  Payment via GCash or Maya. Upload proof of payment after checkout.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
